import { Button } from '@mui/material';
import { closeSnackbar, type SnackbarKey } from 'notistack';
import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilCallback } from 'recoil';
import { currentStructurePeekState } from '~/state/atoms/structureSet/roiAtoms';
import type { ContourEditStep } from '~/domain/undo';
import { enqueueSnackbar } from '~/utils/notifications';
import {
    captureRecordedContourEdit,
    undoContourEditIfCurrent,
} from './useUndoRedo';
import type { RoiId } from '~/domain/changes/ids';

// ── An Undo offered on the report of a deletion ──────────────────────
//
// A deletion that commits without a result to check first has to carry
// its own way back, and the report is the only surface it has. That
// affordance outlives the edit it names: the toast stays on screen while
// the user carries on editing, and a plain undo would by then revert
// whichever edit is newest — a different edit from the one the button is
// labelled with. So the step is captured at the write and the offer
// addresses THAT step, declining once it is no longer current.
//
// One live offer at a time. Three deletions in quick succession would
// otherwise leave three toasts up, each labelled Undo, of which only the
// newest can act — the older two decline, correctly but confusingly.

/** The offer's outstanding snackbar, so a newer one can retire it. */
let liveOfferKey: SnackbarKey | undefined;

/**
 * Retire the Undo offer currently on screen, if any. Called as a new
 * deletion is dispatched rather than when its toast appears: the report
 * is raised inside the tracked write, so this is the last moment the
 * caller controls.
 */
export function closeLiveUndoOffer(): void {
    if (liveOfferKey !== undefined) {
        closeSnackbar(liveOfferKey);
        liveOfferKey = undefined;
    }
}

export interface ContourEditUndoOfferMessages {
    /** Label on the report's Undo button. */
    undoLabelId: string;
    /** Shown when the offer has lapsed — the ROI has been edited since. */
    unavailableMessageId: string;
}

/**
 * The Undo a deletion's own report carries.
 *
 * Usage is a pair: hand {@link ContourEditUndoOffer.action} to the
 * success snackbar, and call {@link ContourEditUndoOffer.captureStep}
 * once the tracked write has landed — until then there is no step to
 * revert and the button declines rather than reverting something else.
 */
export interface ContourEditUndoOffer {
    /**
     * The snackbar action, or `undefined` without a structure to address:
     * a button that always declines is worse than no button.
     */
    action: ((key: SnackbarKey) => React.ReactNode) | undefined;
    /**
     * Starts naming the write's own step. Call BEFORE dispatching the
     * write: the step is recorded asynchronously, so the baseline has to
     * be taken while the edit it supersedes is still current.
     */
    captureStep: () => void;
    /** Retires any Undo still on screen. Call before dispatching. */
    closePreviousOffer: () => void;
}

export function useContourEditUndoOffer(
    roiId: RoiId | null,
    messages: ContourEditUndoOfferMessages
): ContourEditUndoOffer {
    const intl = useIntl();
    // A ref, not state: the offer is read from inside a snackbar action
    // rendered outside this component's render cycle, and it is written
    // after an await. Re-rendering on it would buy nothing and a stale
    // closure would revert the wrong edit.
    //
    // The PROMISE is held rather than the step, because the report — and
    // the Undo on it — is on screen before the step is recorded. A click in
    // that window waits for the step instead of finding none and declining
    // an edit that is about to become revertible.
    const stepRef = useRef<Promise<ContourEditStep | undefined> | undefined>(
        undefined
    );

    const captureStep = useCallback(() => {
        stepRef.current =
            roiId === null ? undefined : captureRecordedContourEdit(roiId);
    }, [roiId]);

    // Read at the click, not when the offer was put on screen.
    const isPeeked = useRecoilCallback(
        ({ snapshot }) =>
            () =>
                snapshot.getLoadable(currentStructurePeekState).getValue()
                    ?.roiId === roiId,
        [roiId]
    );

    const undo = useCallback(async () => {
        // An undo of a peeked structure would change a contour the views
        // are not drawing.
        if (isPeeked()) {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'contouring.refused.structurePeeked',
                }),
                { variant: 'warning' }
            );
            return;
        }
        const step = await stepRef.current;
        if (
            roiId === null ||
            step === undefined ||
            !undoContourEditIfCurrent(roiId, step)
        ) {
            enqueueSnackbar(
                intl.formatMessage({ id: messages.unavailableMessageId }),
                { variant: 'warning' }
            );
        }
    }, [intl, isPeeked, messages.unavailableMessageId, roiId]);

    const action = useCallback(
        (key: SnackbarKey) => {
            liveOfferKey = key;
            return (
                <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                        closeSnackbar(key);
                        liveOfferKey = undefined;
                        void undo();
                    }}
                >
                    {intl.formatMessage({ id: messages.undoLabelId })}
                </Button>
            );
        },
        [intl, messages.undoLabelId, undo]
    );

    // Memoised because callers hold it in their own useCallback deps: a
    // fresh object every render would rebuild every handler that reports
    // through it.
    return useMemo(
        () => ({
            action: roiId === null ? undefined : action,
            captureStep,
            closePreviousOffer: closeLiveUndoOffer,
        }),
        [roiId, action, captureStep]
    );
}
